export type ApplicationStatus = 'pending' | 'approved' | 'rejected' | 'needs_changes' | 'suspended';

export interface RadioApplication {
  id: string;
  radioName: string;
  slogan: string;
  frequency: string;
  planId: 'free' | 'pro' | 'premium';
  planName: string;
  status: ApplicationStatus;
  submittedAt: string;
  updatedAt: string;
  streamUrl: string;
  category: string;
  country: string;
  city: string;
  teamComments: TeamComment[];
  rejectionReason?: string;
  changesRequested?: string[];
  suspensionReason?: string;
  reviewerName?: string;
}

export interface TeamComment {
  id: string;
  author: string;
  role: string;
  text: string;
  date: string;
  isInternal: boolean;
}

export const statusConfig: Record<ApplicationStatus, {
  label: string;
  color: string;
  bgClass: string;
  textClass: string;
  borderClass: string;
  icon: string;
  description: string;
}> = {
  pending: {
    label: 'Pendiente de aprobación',
    color: 'amber',
    bgClass: 'bg-amber-50',
    textClass: 'text-amber-700',
    borderClass: 'border-amber-200',
    icon: 'ri-time-line',
    description: 'Tu solicitud está siendo revisada por nuestro equipo.',
  },
  approved: {
    label: 'Aprobada',
    color: 'green',
    bgClass: 'bg-green-50',
    textClass: 'text-green-700',
    borderClass: 'border-green-200',
    icon: 'ri-check-double-line',
    description: 'Tu radio ha sido aprobada y está lista para transmitir.',
  },
  rejected: {
    label: 'Rechazada',
    color: 'red',
    bgClass: 'bg-red-50',
    textClass: 'text-red-700',
    borderClass: 'border-red-200',
    icon: 'ri-close-circle-line',
    description: 'Tu solicitud no ha sido aprobada. Revisa los comentarios del equipo.',
  },
  needs_changes: {
    label: 'Necesita cambios',
    color: 'orange',
    bgClass: 'bg-orange-50',
    textClass: 'text-orange-700',
    borderClass: 'border-orange-200',
    icon: 'ri-edit-line',
    description: 'El equipo ha solicitado modificaciones antes de aprobar tu radio.',
  },
  suspended: {
    label: 'Suspendida',
    color: 'red',
    bgClass: 'bg-red-50',
    textClass: 'text-red-700',
    borderClass: 'border-red-200',
    icon: 'ri-forbid-line',
    description: 'Tu radio ha sido suspendida temporalmente.',
  },
};

export const applicationTimeline: { status: ApplicationStatus; label: string }[] = [
  { status: 'pending', label: 'Solicitud recibida' },
  { status: 'pending', label: 'En revisión' },
  { status: 'needs_changes', label: 'Cambios solicitados' },
  { status: 'approved', label: 'Aprobada' },
  { status: 'rejected', label: 'Rechazada' },
  { status: 'suspended', label: 'Suspendida' },
];

// Demo applications — one per state, with different plans
const baseApp = {
  radioName: 'Radio Estrella 94.3',
  slogan: 'La que suena diferente',
  frequency: '94.30 RFW',
  submittedAt: '2026-06-01',
  streamUrl: 'https://stream.radioestrella.com:8000/live',
  category: 'Música',
  country: 'Argentina',
  city: 'Buenos Aires',
};

const freeBase = { planId: 'free' as const, planName: 'Free' };
const proBase = { planId: 'pro' as const, planName: 'Pro' };
const premiumBase = { planId: 'premium' as const, planName: 'Premium' };

export const demoApplications: RadioApplication[] = [
  {
    id: 'app-001',
    ...baseApp,
    ...freeBase,
    status: 'pending',
    updatedAt: '2026-06-01',
    teamComments: [
      {
        id: 'c1',
        author: 'Sistema',
        role: 'Automático',
        text: 'Tu solicitud fue recibida correctamente. El equipo de revisión la evaluará en las próximas 24 a 48 horas.',
        date: '2026-06-01',
        isInternal: false,
      },
    ],
    reviewerName: 'María García',
  },
  {
    id: 'app-002',
    ...baseApp,
    ...proBase,
    planName: 'Pro',
    frequency: '94.30 RFW',
    status: 'approved',
    updatedAt: '2026-06-03',
    teamComments: [
      {
        id: 'c1',
        author: 'Sistema',
        role: 'Automático',
        text: 'Tu radio fue activada inmediatamente. Bienvenido a RadioFrecuenciaWeb. Ya podés acceder a tu panel de administración.',
        date: '2026-06-01',
        isInternal: false,
      },
      {
        id: 'c2',
        author: 'María García',
        role: 'Revisora',
        text: 'Revisión posterior completada. Todo en orden. Excelente calidad de stream y perfil bien armado. ¡Seguí así!',
        date: '2026-06-03',
        isInternal: false,
      },
    ],
    reviewerName: 'María García',
  },
  {
    id: 'app-003',
    ...baseApp,
    ...freeBase,
    frequency: '88.70 RFW',
    status: 'rejected',
    updatedAt: '2026-06-04',
    rejectionReason: 'El stream no se encuentra activo. Realizamos tres intentos de conexión en diferentes horarios y no obtuvimos señal.',
    teamComments: [
      {
        id: 'c1',
        author: 'Sistema',
        role: 'Automático',
        text: 'Tu solicitud fue recibida correctamente.',
        date: '2026-06-01',
        isInternal: false,
      },
      {
        id: 'c2',
        author: 'Carlos Ruiz',
        role: 'Revisor técnico',
        text: 'Intenté conectar al stream en tres ocasiones distintas: 02/06 10:00, 02/06 18:00, 03/06 14:00. En ninguna obtuve respuesta del servidor.',
        date: '2026-06-03',
        isInternal: true,
      },
      {
        id: 'c3',
        author: 'Carlos Ruiz',
        role: 'Revisor',
        text: 'Lamentablemente no podemos aprobar tu radio porque el stream no responde. Revisá que tu servidor esté funcionando y que la URL sea correcta, luego volvé a enviar la solicitud.',
        date: '2026-06-04',
        isInternal: false,
      },
    ],
    reviewerName: 'Carlos Ruiz',
  },
  {
    id: 'app-004',
    ...baseApp,
    ...proBase,
    radioName: 'Radio Onda Digital',
    slogan: 'Conectando tu mundo',
    frequency: '99.50 RFW',
    submittedAt: '2026-06-02',
    planName: 'Pro',
    status: 'needs_changes',
    updatedAt: '2026-06-05',
    changesRequested: [
      'El logo tiene baja resolución. Por favor subí una imagen de al menos 512×512 px.',
      'La descripción de la radio está vacía. Agregá al menos un párrafo contando de qué trata.',
      'El stream está en 64 kbps. Recomendamos al menos 128 kbps para una mejor experiencia.',
    ],
    teamComments: [
      {
        id: 'c1',
        author: 'Sistema',
        role: 'Automático',
        text: 'Tu radio fue activada inmediatamente por ser plan Pro. Estamos haciendo la revisión de calidad.',
        date: '2026-06-02',
        isInternal: false,
      },
      {
        id: 'c2',
        author: 'Laura Fernández',
        role: 'Revisora de calidad',
        text: 'Tu radio ya está transmitiendo, pero encontramos algunos puntos que mejorar para brindar una mejor experiencia a tus oyentes. Revisá los cambios solicitados.',
        date: '2026-06-05',
        isInternal: false,
      },
    ],
    reviewerName: 'Laura Fernández',
  },
  {
    id: 'app-005',
    ...baseApp,
    ...premiumBase,
    radioName: 'Frecuencia Premium 100',
    slogan: 'La excelencia en radio',
    frequency: '100.00 RFW',
    submittedAt: '2026-05-15',
    planName: 'Premium',
    status: 'suspended',
    updatedAt: '2026-06-07',
    suspensionReason: 'Contenido inapropiado detectado en la transmisión del día 06/06/2026. La radio permanecerá suspendida hasta que se revise el caso.',
    teamComments: [
      {
        id: 'c1',
        author: 'Sistema',
        role: 'Automático',
        text: 'Tu radio fue activada inmediatamente. Bienvenido a RadioFrecuenciaWeb.',
        date: '2026-05-15',
        isInternal: false,
      },
      {
        id: 'c2',
        author: 'María García',
        role: 'Revisora',
        text: 'Revisión de contenido del 06/06/2026. Se detectó material que infringe los términos de uso. Se procede a la suspensión preventiva.',
        date: '2026-06-07',
        isInternal: true,
      },
      {
        id: 'c3',
        author: 'María García',
        role: 'Administradora',
        text: 'Tu radio ha sido suspendida temporalmente por infringir nuestros términos de uso. Revisá el motivo y contactanos a soporte@radiofrecuenciaweb.com para resolver la situación.',
        date: '2026-06-07',
        isInternal: false,
      },
    ],
    reviewerName: 'María García',
  },
];

// Helper: next steps per status and plan
export function getNextSteps(status: ApplicationStatus, planId: 'free' | 'pro' | 'premium'): { title: string; description: string; icon: string; action?: string }[] {
  const isFree = planId === 'free';

  switch (status) {
    case 'pending':
      return isFree
        ? [
            { title: 'Esperar la revisión', description: 'Nuestro equipo revisa tu solicitud. Recibirás un correo en 24-48 horas.', icon: 'ri-time-line' },
            { title: 'Mantener el stream activo', description: 'Asegurate de que tu stream esté funcionando. El equipo lo verificará.', icon: 'ri-sound-module-line' },
            { title: 'Preparar tu contenido', description: 'Mientras tanto, prepará tu programación, logo y perfil para cuando estés activo.', icon: 'ri-file-list-3-line' },
          ]
        : [
            { title: 'Acceder al panel', description: 'Tu radio ya está activa. Entrá al panel de administración y empezá a configurar.', icon: 'ri-dashboard-line', action: 'Ir al panel' },
            { title: 'Configurar tu perfil', description: 'Completá tu perfil, subí tu logo y personalizá tu página pública.', icon: 'ri-user-settings-line' },
            { title: 'Iniciar tu primera transmisión', description: 'Conectá tu stream y hace tu primera transmisión en vivo.', icon: 'ri-broadcast-line' },
          ];

    case 'approved':
      return [
        { title: 'Acceder al panel', description: 'Administrá tu radio desde el panel de control.', icon: 'ri-dashboard-line', action: 'Ir al panel' },
        { title: 'Crear tu primera publicación', description: 'Anunciá que estás al aire. Publicá fotos, eventos y más.', icon: 'ri-article-line' },
        { title: 'Invitar oyentes', description: 'Compartí tu frecuencia con tu audiencia y redes sociales.', icon: 'ri-share-line' },
      ];

    case 'rejected':
      return [
        { title: 'Revisar el motivo', description: 'Leé los comentarios del equipo para entender qué falló.', icon: 'ri-file-search-line' },
        { title: 'Corregir el problema', description: 'Solucioná lo indicado: stream, perfil, o lo que corresponda.', icon: 'ri-tools-line' },
        { title: 'Volver a enviar', description: 'Una vez corregido, volvé a registrar tu radio.', icon: 'ri-refresh-line', action: 'Volver a registrar' },
      ];

    case 'needs_changes':
      return [
        { title: 'Revisar los cambios solicitados', description: 'El equipo te pidió modificaciones específicas.', icon: 'ri-file-list-2-line' },
        { title: 'Aplicar las correcciones', description: isFree ? 'Hacé los cambios y tu solicitud seguirá en revisión.' : 'Hacé los cambios desde tu panel de administración.', icon: 'ri-edit-box-line' },
        { title: 'Notificar al equipo', description: 'Cuando termines, avisanos para que revisemos de nuevo.', icon: 'ri-mail-send-line' },
      ];

    case 'suspended':
      return [
        { title: 'Revisar el motivo de suspensión', description: 'Leé atentamente la razón de la suspensión.', icon: 'ri-error-warning-line' },
        { title: 'Contactar a soporte', description: 'Escribinos a soporte@radiofrecuenciaweb.com para resolverlo.', icon: 'ri-customer-service-line', action: 'Contactar soporte' },
        { title: 'Corregir el contenido', description: 'Revisá y ajustá tu contenido para cumplir con los términos.', icon: 'ri-shield-check-line' },
      ];
  }
}